<template>
  <v-card>
    <v-card-title class="primary white--text">
      <span class="headline"> {{ $t('newVersion') }} </span>
    </v-card-title>
    <v-card-text>
      <v-card outlined class="mt-6 mb-6">
        <v-card-text class="mt-n4">
          <v-radio-group v-model="creationStrategy">
            <v-radio :label="$t('blank')" :value="CreationStrategy.Blank" class="pb-3"></v-radio>
            <v-radio :label="$t('basedOn')" :value="CreationStrategy.FromVersion"></v-radio>
          </v-radio-group>

          <v-select v-if="initialized"
          v-model="createFromVersion"
          :label="$t('version')"
          :disabled="creationStrategy !== CreationStrategy.FromVersion"
          class="ml-10"
          :items="versions"
          :item-text="(item) => item.localUuid || item.versionedRateSheetId"
          return-object>
            <template #selection="{ item }">
              <span>{{ getTextForItem(item) }}</span>
            </template>
            <template #item="{ item }">
              <span>{{ getTextForItem(item) }}</span>
            </template>
          </v-select>
        </v-card-text>
      </v-card>

      <DatePicker v-if="initialized"
        dense
        :dateLabel="$t('effective')"
        :startDate="effectiveDate"
        @date-picked="onDateChosen"
        :isValid="datePickerIsValid"
        :disabled="disableDateSelection"/>
    </v-card-text>
    <v-card-actions class="pb-4 px-0 mt-4">
      <v-row justify="space-between">
        <v-col cols="auto">
          <v-btn
          text
          outlined
          color="secondary"
          @click="creationCanceled">
            {{ $t('cancel') }}
          </v-btn>
        </v-col>

        <v-col cols="auto">
          <v-btn
          :disabled="!isValid"
          color="primary"
          @click="creationConfirmed">
            {{ $t('confirm') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>
<script>
import { mapGetters } from 'vuex'
import { formatDate } from '@/utils/DateFormatter.js'
import moment from 'moment'

const CreationStrategy = {
  Blank: 0,
  FromVersion: 1
}

export default {
  name: 'RateSheetDefinitionVersionCreator',

  components: {
    DatePicker: () => import('@/components/helper/DatePicker.vue')
  },

  props: {
    versions: {
      type: Array,
      required: false,
      default: undefined
    },
    propCreateFromVersion: {
      type: Object,
      required: false,
      default: null
    }
  },

  data: () => ({
    initialized: false,
    CreationStrategy,
    creationStrategy: CreationStrategy.FromVersion,
    createFromVersion: null,
    newVersion: {
      effectiveDate: moment(),
      rateSheetDefinitionLines: []
    },
    effectiveDate: null
  }),

  mounted () {
    this.intialize()
  },

  computed: {
    ...mapGetters('rate-sheet-definition', ['accountRateSheetDefinitions']),

    isValid () {
      if (!this.versions) return true
      return this.versions.every(v => {
        return moment(v.effectiveDate).isBefore(moment(this.effectiveDate))
      })
    },

    disableDateSelection () {
      return false
    },

    datePickerIsValid () {
      return this.isValid || this.$t('mustBeLaterThanExistingVersions')
    }
  },

  methods: {
    formatDate,

    intialize () {
      this.createFromVersion = this.propCreateFromVersion
      this.effectiveDate = this.getDateForNewVersion()
      this.initialized = true
    },

    getDateForNewVersion () {
      return this.versions?.length > 0
        ? moment().add(1, 'week').startOf('day')
        : moment().startOf('day')
    },

    onDateChosen (date) {
      this.effectiveDate = date
    },

    creationCanceled () {
      this.$emit('creation-canceled')
    },

    creationConfirmed () {
      this.$emit('creation-confirmed', this.getNewVersion())
    },

    getNewVersion () {
      switch (this.creationStrategy) {
        case CreationStrategy.Blank:
          return this.getBlankVersion()
        case CreationStrategy.FromVersion:
          return this.getFromVersion()
      }
    },

    getBlankVersion () {
      return {
        effectiveDate: this.effectiveDate,
        rateSheetDefinitionLines: []
      }
    },

    getFromVersion () {
      return {
        effectiveDate: this.effectiveDate,
        rateSheetDefinitionLines: this.createFromVersion.rateSheetDefinitionLines.map(line => ({
          ...line,
          rateSheetDefinitionLineId: null
        }))
      }
    },

    getTextForItem (item) {
      return formatDate(item.effectiveDate)
    }
  }
}
</script>

<style>
.rate-sheet-definition-version-selector .v-select__selections input {
  max-width: 0px;
}
</style>
